import {Badge, Button, Card, Container, Group, Modal, Paper, SimpleGrid, Stack} from "@mantine/core";
import React from "react";
import {useDoc, usePouch} from "use-pouchdb";
import {IconTrash, IconDownload} from "@tabler/icons-react";
import {Link} from "react-router-dom";
import log from "loglevel";
import {useFeatureLimit} from "../hooks/useFeatures";
import {exportDiagramAsJSON} from "../../util/functions/exportDiagramAsJSON";

// Helper to get the correct URL path based on storage type
function getDiagramPath(diagram: any): string {
    const storageType = diagram.storageType || 'public'; // Default to public for backwards compat
    return `/db/${storageType}/${diagram._id}`;
}

// Helper to get badge color and text for storage type
function getStorageTypeBadge(storageType: string): { color: string; label: string } {
    switch (storageType) {
        case 'local':
            return { color: 'gray', label: 'Local' };
        case 'public':
            return { color: 'green', label: 'Public' };
        case 'private':
            return { color: 'blue', label: 'Private' };
        default:
            return { color: 'green', label: 'Public' }; // Default for backwards compat
    }
}

export default function ManageDiagramsModal({openCreate, manageOpened, closeManage}) {
    const logger = log.getLogger('manageDiagramsModal');
    const {doc: diagram, error} = useDoc('directory', {}, {_id: 'directory', diagrams: []});
    const db = usePouch();
    const maxDiagrams = useFeatureLimit('maxDiagrams');
    if (error) {

        if (error.status === 404) {
            logger.info('Creating new diagram document');
            db.put({_id: 'directory', diagrams: []});
        } else {
            logger.error('Error getting diagram document', error);
        }
        return <></>;
    }
    const diagrams = diagram.diagrams || [];

    const handleExportDiagram = async (diagramId: string) => {
        try {
            await exportDiagramAsJSON(diagramId);
            logger.info(`Diagram ${diagramId} exported successfully`);
        } catch (error) {
            logger.error('Failed to export diagram:', error);
        }
    };

    const cards = diagrams.map((diagram) => {
        const badgeInfo = getStorageTypeBadge(diagram.storageType);
        const diagramPath = getDiagramPath(diagram);

        return (
            <Card key={diagram._id}>
                <Card.Section>
                    <Group justify="space-between" p="xs">
                        <Container w={400} h={64}>{diagram.name}</Container>
                        <Badge color={badgeInfo.color} size="sm">{badgeInfo.label}</Badge>
                    </Group>
                </Card.Section>
                <Card.Section>
                    <Container w={512} h={128}>
                        {diagram.description}
                    </Container>
                </Card.Section>
                <Card.Section>
                    <Group justify="space-evenly">
                        <Button component={Link} key="examples" to={diagramPath} p={5} c="myColor"
                                bg="none">Select</Button>

                        <Button
                            onClick={() => handleExportDiagram(diagram._id)}
                            variant="light"
                            size="xs"
                            title="Export as JSON">
                            <IconDownload size={16}/>
                        </Button>

                        <Button bg="red" size="xs"><IconTrash size={16}/></Button>
                    </Group>
                </Card.Section>
            </Card>
        )
    });

    const buildCreateButton = () => {
        // Check against the configured maxDiagrams limit
        const hasReachedLimit = maxDiagrams > 0 && diagrams.length >= maxDiagrams;

        if (!hasReachedLimit) {
            return <Button size="lg" onClick={openCreate} disabled={false}>Create</Button>
        } else {
            return (<Stack>
                <Button key="create" size="lg" disabled={true}>Create</Button>
                <Paper key="upgrademessage">You've reached the max number of diagrams ({maxDiagrams}) for your current tier.</Paper>
                <Button key="upgradebutton" size="xl">Upgrade To Pro</Button>
            </Stack>)
        }
    }
    return (
        <Modal opened={manageOpened} size="lg" onClose={closeManage}>
            <h1>Select a Diagram</h1>
            <SimpleGrid cols={3}>
                {cards}
            </SimpleGrid>
            {buildCreateButton()}
        </Modal>

    )
}